import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Close from "@mui/icons-material/Close";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import {
  SCOUT_POSTHOG_EVENTS,
  SCOUT_UPSELL_PLACEMENTS,
} from "encamp-shared/src/instrumentation/events";
import { useState } from "react";

import type { ScoutProUpsell } from "./scoutProUpsell";

import { capturePosthog } from "../../../../instrumentation/posthog";
import { scoutHostToRuntime } from "../../../../instrumentation/scoutHost";
import { SCOUT_SUPPORT_ARTICLE } from "../ScoutInfoPanel";
import { useScoutHostContext } from "./ScoutHostContext";
import { useScoutProUpsellContext } from "./ScoutProUpsellContext";

type Props = {
  upsell: ScoutProUpsell;
};

const SCOUT_PRO_UPSELL_BODY =
  "Scout can go even further with Pro. Upload SDSs, permits, and manifests, and Scout reads them for you — pulling out chemical, waste, and facility data automatically. With Pro, Scout also looks up your data, taps into Document Intelligence and Insights, and can even take action on your behalf, not just answer questions.";

export function ScoutProUpsellCard({ upsell }: Props) {
  const context = useScoutProUpsellContext();
  const { host } = useScoutHostContext();
  const [hasError, setHasError] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const dismissed = context?.dismissedUpsells[upsell.upsellId] === "dismissed";
  const hasContext = context !== undefined;

  if (dismissed) {
    return null;
  }

  const handleDismiss = async () => {
    if (!context || isDismissing) return;
    setHasError(false);
    setIsDismissing(true);
    try {
      await context.onDismiss({ upsellId: upsell.upsellId });
    } catch {
      setHasError(true);
      setIsDismissing(false);
    }
  };

  const handleLearnMoreClick = () => {
    capturePosthog(SCOUT_POSTHOG_EVENTS.PRO_UPSELL_LEARN_MORE_CLICKED, {
      upsell_id: upsell.upsellId,
      capability: upsell.capability,
      host: scoutHostToRuntime(host),
      placement: SCOUT_UPSELL_PLACEMENTS.ASSISTANT_MESSAGE_CARD,
    });
  };

  return (
    <Box
      data-testid="scout-pro-upsell-card"
      sx={(theme) => ({
        mt: 1,
        p: 2,
        pb: 1.5,
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        bgcolor: theme.palette.background.paper,
        position: "relative",
      })}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <AutoAwesome color="primary" fontSize="small" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Unlock this with Scout Pro
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {SCOUT_PRO_UPSELL_BODY}
            </Typography>
          </Box>
          <IconButton
            aria-label="Dismiss Scout Pro upsell"
            size="small"
            disabled={!hasContext || isDismissing}
            onClick={handleDismiss}
            sx={{ ml: 1, mt: -0.5 }}
          >
            {isDismissing ? <CircularProgress size={16} /> : <Close fontSize="small" />}
          </IconButton>
        </Stack>
        {hasError ? (
          <Alert severity="error">Something went wrong dismissing this. Please try again.</Alert>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            href={SCOUT_SUPPORT_ARTICLE}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            size="small"
            onClick={handleLearnMoreClick}
          >
            LEARN MORE
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
